import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { normalizeSearchText } from "@/lib/utils/searchUtils";
import type { PurchaseExpenseCategoryOption } from "@/hooks/usePurchaseExpenseCategories";

/**
 * C-4 (R-COMPRAS.10): Combobox com busca para a Categoria de Despesa dos trilhos de compra
 * (molde shadcn Command do SupplierCombobox). Apresentacional: a lista já chega filtrada e
 * ordenada por uso de usePurchaseExpenseCategories. Contrato de valor = id da categoria.
 */
interface PurchaseExpenseCategoryComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: PurchaseExpenseCategoryOption[];
  isLoading?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function PurchaseExpenseCategoryCombobox({
  value,
  onChange,
  options,
  isLoading = false,
  placeholder = "Selecione para vincular ao DRE...",
  disabled = false,
}: PurchaseExpenseCategoryComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Busca local sem acento/pontuação; a ORDEM por uso da lista é preservada
  const filtered = useMemo(() => {
    const termo = normalizeSearchText(searchQuery);
    if (!termo) return options;
    return options.filter((o) => normalizeSearchText(o.name).includes(termo));
  }, [options, searchQuery]);

  const selected = options.find((o) => o.id === value);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearchQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground")}
        >
          <span className="truncate">
            {selected ? selected.name : isLoading ? "Carregando..." : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] min-w-[320px] p-0 z-[9999] bg-background pointer-events-auto"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Buscar categoria..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? "Carregando..." : "Nenhuma categoria encontrada."}
            </CommandEmpty>
            <CommandGroup>
              {filtered.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => {
                    onChange(option.id === value ? "" : option.id);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn("mr-2 h-4 w-4", value === option.id ? "opacity-100" : "opacity-0")}
                  />
                  <span className="flex-1 truncate">{option.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
            {isLoading && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
